
import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import PageHeader from '../components/common/PageHeader';
import SectionHeading from '../components/ui/SectionHeading';
import TimelineItem from '../components/services/TimelineItem';
import ServiceSubscribeCard from '../components/services/ServiceSubscribeCard';
import GlossaryAutoLinker from '../components/glossary/GlossaryAutoLinker';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { MessageCircle, Shield, BarChart3, Users, Heart, ArrowRight, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

const faqData = [
  {
    value: 'timeline',
    question: 'How long does the AI implementation process take?',
    answer: 'AI implementation timelines typically range from two to four weeks for clearly scoped, low-to-moderate complexity projects. The process follows four phases: discovery and baseline measurement (days 1–3), strategy and use-case prioritisation (days 4–6), guided implementation with staff training (days 7–12), and go-live with initial review (days 13–14). Complex integrations, regulated environments, or data migrations may require extended timelines, which are assessed during the discovery phase. Structured reviews at 30, 60, and 90 days follow every deployment to measure performance against baseline metrics.',
  },
  {
    value: 'pricing',
    question: 'What are the typical costs for AI implementation?',
    answer: 'AI implementation costs for small-to-medium businesses in New Zealand typically range from $900 to $25,000+, depending on scope and complexity. Discovery and AI roadmap projects start from $900 (most fall between $900–$2,500). Infrastructure build and implementation costs vary by project, with most SME builds ranging from $6,500 to $25,000+. Ongoing oversight, maintenance, and optimisation is available from $120–$650 per month. A detailed ROI model is provided during the discovery phase so projected savings can be evaluated before committing to implementation.',
  },
  {
    value: 'technical',
    question: 'Do I need technical expertise to use AI solutions?',
    answer: 'No technical expertise is required to use business AI solutions. AI tools are designed for business users, not developers — the technical complexity of integration, configuration, and deployment is handled entirely during implementation. Staff training is included as part of every project to ensure teams are confident using new tools. Ongoing technical support is available post-launch for any questions or adjustments.',
  },
  {
    value: 'oversight',
    question: 'How is human oversight maintained in AI systems?',
    answer: 'Human-in-the-loop oversight is a core design principle in responsible AI deployment. Critical decisions retain human review, AI outputs are validated before external use, and governance frameworks define escalation paths and decision boundaries. Rollback procedures are built into every deployment so AI systems can be paused or reversed at any time. This approach aligns with MBIE\'s principles for responsible AI adoption in New Zealand.',
  },
  {
    value: 'security',
    question: 'How is data security and privacy handled in AI implementations?',
    answer: 'Data security in AI implementations follows the SAFE-NZ framework: Strip personal identifiers, Ask for evidence, Fact-check critical claims, and Export to secure systems. Client data is never used to train AI models. Implementations include role-based access control, encryption at rest and in transit, and compliance with the New Zealand Privacy Act 2020. Data handling policies are documented during the strategy phase and reviewed at each optimisation cycle.',
  },
  {
    value: 'scale',
    question: 'Can AI solutions scale as a business grows?',
    answer: 'AI solutions built on cloud infrastructure and industry-standard integrations scale with business growth without requiring re-architecture. Systems are designed to handle increasing transaction volumes, additional users, and expanded workflows. Performance monitoring is included in ongoing maintenance to identify scaling needs before they become bottlenecks. Capacity adjustments are handled as part of the optimisation review cycle.',
  },
  {
    value: 'support',
    question: 'What support is provided after AI implementation?',
    answer: 'Post-implementation support includes performance monitoring, error detection, workflow updates, compliance oversight, and monthly reporting on outcomes and ROI. Structured reviews are conducted at 30, 60, and 90 days after deployment to compare results against projections and adjust where needed. Priority support is available for urgent issues. Ongoing maintenance plans range from $120 to $650 per month depending on scope and complexity.',
  },
  {
    value: 'roi',
    question: 'How is return on investment measured for AI projects?',
    answer: 'ROI measurement begins during the discovery phase by capturing baseline metrics — time spent on tasks, error rates, and operational costs. These baselines form the benchmark against which post-launch performance is tracked. ROI projections with clear success criteria are created during the strategy phase. Structured reviews at 30, 60, and 90 days compare actual outcomes against projections, and monthly reports provide ongoing visibility into measurable results.',
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer,
    },
  })),
};

const AISolutions: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>AI Solutions for NZ Businesses | ThinkTomorrow.AI</title>
        <meta name="description" content="Responsible AI adoption, governed assistants, and workflow automation built for New Zealand SMEs. Clear scope, measurable outcomes, ongoing support." />
        <link rel="canonical" href="https://thinktomorrow.ai/ai-solutions" />
        <meta property="og:title" content="AI Solutions for NZ Businesses | ThinkTomorrow.AI" />
        <meta property="og:description" content="Responsible AI adoption and workflow automation for New Zealand SMEs." />
        <meta property="og:url" content="https://thinktomorrow.ai/ai-solutions" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navbar />
      <main className="min-h-screen pt-16 md:pt-24">
        <PageHeader 
          title="Responsible AI Solutions for Your Business"
          subtitle="Human-centred AI adoption with clear scope, measurable outcomes, and ongoing support. Aligned with New Zealand's AI strategy."
        />
        
        {/* AI Adoption Timeline Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading 
              title="AI Adoption Timeline – What Happens and When" 
              subtitle="A structured, responsible approach to getting AI working for your business"
              centered
              gradient
            />
            
            <div className="max-w-4xl mx-auto mt-12 relative">
              {/* Timeline line */}
              <div className="absolute h-full w-1 bg-brand-teal left-1/2 -translate-x-1/2 top-0" style={{ zIndex: 1 }}></div>
              
              {/* 14-Day Goal Header */}
              <div className="flex justify-center mb-12 relative">
                <div className="bg-white rounded-xl border border-brand-teal/30 p-6 text-center shadow-md relative z-10 max-w-md">
                  <h3 className="text-gray-600 mb-1">Our Goal</h3>
                  <div className="text-4xl font-bold text-brand-teal mb-2">
                    14 Days
                  </div>
                  <p className="text-sm text-gray-700 font-medium">Initial AI use-cases live and operational</p>
                </div>
              </div>

              {/* Scope Qualifier */}
              <div className="flex justify-center mb-10 relative z-10">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl">
                  <div className="flex items-start">
                    <Info size={18} className="text-blue-600 mr-2 mt-0.5 shrink-0" />
                    <p className="text-sm text-blue-800">
                      <strong>Scope note:</strong> This timeline applies to clearly scoped, low-to-moderate complexity use cases. 
                      Complex integrations, regulated environments, or data migrations may require extended timelines—we'll discuss this during discovery.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-12 relative">
                <TimelineItem 
                  step={1}
                  title="Discovery & AI Readiness Evaluation" 
                  timeframe="Days 1-3"
                  description={
                    <GlossaryAutoLinker>
                      We conduct an AI Readiness Evaluation of your workflows, capture baseline metrics (time, cost, error rates), and identify where AI can add genuine value. Sometimes the smartest first step is protecting stable processes.
                    </GlossaryAutoLinker>
                  }
                  alignment="left"
                />
                
                <TimelineItem 
                  step={2}
                  title="AI Strategy & Use-Case Prioritisation" 
                  timeframe="Days 4-6"
                  description={
                    <GlossaryAutoLinker>
                      We develop your custom AI roadmap, prioritise use-cases by ROI and risk, and design human-in-the-loop oversight. Every recommendation is justifiable, reversible, and proportionate.
                    </GlossaryAutoLinker>
                  }
                  alignment="right"
                />
                
                <TimelineItem 
                  step={3}
                  title="Guided Implementation" 
                  timeframe="Days 7-12"
                  description={
                    <GlossaryAutoLinker>
                      We build and integrate AI solutions — from workflow automation to AI agents — with minimal disruption. Staff training ensures your team is confident. Every deployment includes a rollback plan and human oversight.
                    </GlossaryAutoLinker>
                  }
                  alignment="left"
                />
                
                <TimelineItem 
                  step={4}
                  title="Go-Live & Initial Review" 
                  timeframe="Days 13-14"
                  description="Initial AI use-cases go live. We conduct a post-launch performance review against baseline metrics and ensure staff are comfortable with the new tools."
                  alignment="right"
                />

                <TimelineItem 
                  step={5}
                  title="Ongoing Optimisation & ROI Reviews" 
                  timeframe="30 / 60 / 90 Days"
                  description="Structured reviews at 30, 60, and 90 days to measure ROI against projections, optimise performance, and adjust based on real-world usage. You're never left on your own."
                  alignment="left"
                  isLast
                />
                
                {/* Success Box */}
                <div className="flex justify-center mt-8 relative">
                  <div className="bg-brand-teal text-white rounded-xl p-6 text-center shadow-md max-w-md relative z-10">
                    <h3 className="text-xl font-bold mb-2">Measurable Results, Ongoing Support</h3>
                    <p className="text-sm">AI working for your business with clear metrics, human oversight, and continuous improvement.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center mt-12">
              <Link to="/services">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="font-semibold border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-white"
                >
                  View Detailed Pricing
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Built for Confidence Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4 font-ibm">Built for Confidence, Not Just Speed</h2>
              <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
                We prioritise responsible AI adoption that delivers long-term value. Our approach is aligned with MBIE's New Zealand Strategy for Artificial Intelligence.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 rounded-full bg-brand-teal/10 flex items-center justify-center mr-3">
                      <Shield size={20} className="text-brand-teal" />
                    </div>
                    <h3 className="text-lg font-semibold">Safety First</h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    <GlossaryAutoLinker>
                      Every AI deployment includes human oversight, rollback plans, and governance frameworks. We assess whether AI is genuinely beneficial before recommending it.
                    </GlossaryAutoLinker>
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 rounded-full bg-brand-teal/10 flex items-center justify-center mr-3">
                      <BarChart3 size={20} className="text-brand-teal" />
                    </div>
                    <h3 className="text-lg font-semibold">Measurable Outcomes</h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    We capture baseline metrics during discovery and track performance against them. Monthly reports show real ROI, not just activity metrics.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 rounded-full bg-brand-teal/10 flex items-center justify-center mr-3">
                      <Users size={20} className="text-brand-teal" />
                    </div>
                    <h3 className="text-lg font-semibold">People-Centred</h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    AI augments your team, it doesn't replace them. We invest in staff training and ensure your people are confident and capable with new tools.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 rounded-full bg-brand-teal/10 flex items-center justify-center mr-3">
                      <Heart size={20} className="text-brand-teal" />
                    </div>
                    <h3 className="text-lg font-semibold">Long-Term Trust</h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    We're invested in your long-term success, not just the initial deployment. Ongoing support, optimisation, and honest advice—even when that means doing nothing.
                  </p>
                </div>
              </div>
              
              <div className="mt-10 bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-6 text-center">
                <p className="text-gray-700">
                  <strong>Our commitment:</strong> All AI work we deliver is justifiable, reversible, proportionate, and defensible—aligned with MBIE's AI adoption principles.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Strategic Services Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading 
              title="Other AI Strategic Services" 
              subtitle="Targeted solutions to address specific business needs"
              centered
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mt-10">
              <ServiceSubscribeCard 
                productId="market-penetration"
                stripeProductId="prod_S0LWGqQlFq72ep"
                title="Deep Dive Market Research & Penetration Strategies"
                description="Gain comprehensive market insights for strategic advantage"
                price={450}
                isSubscription={false}
                showContactInfo={true}
                features={[
                  "Thorough market analysis",
                  "Competitor insights",
                  "Clear market-entry strategies",
                  "Customized recommendations",
                  "Ideal Customer Profiles (ICPs)",
                  "Pain points",
                  "Market sizes TAM, SAM and SOM"
                ]}
              />
              
              <ServiceSubscribeCard 
                productId="consultation"
                stripeProductId="prod_S0La9juSL63P2k"
                title="Idea Validation and recommendations"
                description={
                  <>
                    A full evaluation of your idea with our inhouse tool{' '}
                    <a 
                      href="https://ideaxroad.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-brand-teal hover:underline font-semibold"
                    >
                      IdeaXroad
                    </a>
                    .
                  </>
                }
                price={49}
                isSubscription={false}
                showContactInfo={true}
                features={[
                  "14 - 24 page report on your idea's validity",
                  "Wedge evaluation",
                  "Scored across 6 pillars",
                  "Pivots to strengthen the idea",
                  "Potential user pain points",
                  "Micro experiments to help validation",
                  "30 day action plan post verdict",
                  "Clear, Go / Test / No Go verdict"
                ]}
                link="https://ideaxroad.com/"
              />
              
              <ServiceSubscribeCard 
                productId="website-app-dev"
                title="Website & App Development"
                description="Launch a modern, AI-ready website or web app without a heavy upfront cost. Our basic websites use proven templates, include hosting and support, and start from $70/month with no setup fee. As you add more functionality, integrations or app-like features, we design and build what you need on top of a solid, scalable foundation."
                customPrice="Websites from $70/month"
                isSubscription={true}
                showContactInfo={true}
                ctaText="Explore Website & App Options"
                features={[
                  "Professionally designed, mobile-responsive websites",
                  "No upfront build cost for simple brochure-style sites",
                  "SEO-friendly structure and on-page optimisation basics",
                  "Built-in lead capture, forms and contact flows",
                  "Integration-ready for chatbots, AI agents and automation",
                  "Hosting, security updates and technical maintenance"
                ]}
                note="Basic websites have no upfront build fee and start from $70/month. More complex websites and AI-enabled web apps involve a one-off setup fee based on scope, integrations and functionality."
              />
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <Helmet>
              <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            </Helmet>
            <SectionHeading 
              title="Frequently Asked Questions" 
              subtitle="Clear answers to common questions about AI implementation for businesses"
              centered
            />
            
            <div className="max-w-3xl mx-auto mt-10">
              <Accordion type="single" collapsible className="space-y-4">
                {faqData.map(faq => (
                  <AccordionItem key={faq.value} value={faq.value} className="bg-gray-50 rounded-lg border border-gray-200 px-6">
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-brand-teal">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pt-2">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-brand-navy to-brand-teal/90 text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to explore responsible AI adoption?</h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto">Contact us for a conversation about your business goals. We'll assess where AI can genuinely help—and where it might not be the right fit.</p>
            <div className="flex justify-center">
              <Button 
                href="/contact" 
                variant="outline" 
                size="lg" 
                className="font-bold border-white text-white hover:bg-white/10"
                icon={<MessageCircle size={18} className="text-white" />}
                iconPosition="left"
              >
                Start a Conversation
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AISolutions;
